import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Medication as MedicationIcon,
  Healing as HealingIcon,
  Vaccines as VaccineIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { DischargeSummaryService } from '../../services/dischargeSummaryService';
import { INSTANT_FORMATS, formatCalendarDate } from '../../utils/dateUtils';
import { useFormatInstant } from '../../hooks/useClinicTimezone';

interface AutomationSuggestionsModalProps {
  open: boolean;
  onClose: () => void;
  horseId: string;
  onGenerate: (selectedIds: {
    soapNoteIds: string[];
    medicalRecordIds: string[];
    procedureIds: string[];
    vaccinationIds: string[];
    medicationIds: string[];
  }) => void;
}

const AutomationSuggestionsModal: React.FC<AutomationSuggestionsModalProps> = ({
  open,
  onClose,
  horseId,
  onGenerate,
}) => {
  const [suggestions, setSuggestions] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  // INSTANT renders below go through the clinic's zone (DATE-KINDS §0), never
  // the viewer's browser zone — relief/remote staff would otherwise see a
  // different day than the clinic that wrote the record.
  const formatInstant = useFormatInstant();
  const [selectedIds, setSelectedIds] = useState<{
    soapNoteIds: string[];
    medicalRecordIds: string[];
    procedureIds: string[];
    vaccinationIds: string[];
    medicationIds: string[];
  }>({
    soapNoteIds: [],
    medicalRecordIds: [],
    procedureIds: [],
    vaccinationIds: [],
    medicationIds: [],
  });

  useEffect(() => {
    if (open && horseId) {
      fetchSuggestions();
    }
  }, [open, horseId]);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const data = await DischargeSummaryService.getSuggestions(horseId);
      setSuggestions(data);
      // Auto-select all suggestions
      setSelectedIds({
        soapNoteIds: data.soapNotes.map((n: any) => n.id),
        medicalRecordIds: data.medicalRecords.map((r: any) => r.id),
        procedureIds: data.procedures.map((p: any) => p.id),
        vaccinationIds: data.vaccinations.map((v: any) => v.id),
        medicationIds: data.medications?.map((m: any) => m.id) || [],
      });
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (type: 'soapNoteIds' | 'medicalRecordIds' | 'procedureIds' | 'vaccinationIds' | 'medicationIds', id: string) => {
    setSelectedIds((prev) => {
      const currentList = prev[type];
      const isSelected = currentList.includes(id);
      return {
        ...prev,
        [type]: isSelected
          ? currentList.filter((itemId) => itemId !== id)
          : [...currentList, id],
      };
    });
  };

  const groupVaccinationsBySession = (vaccinations: any[]) => {
    const sessions = new Map();
    vaccinations.forEach(vacc => {
      const key = `${vacc.dateAdministered}_${vacc.recordId || 'standalone'}`;
      if (!sessions.has(key)) {
        sessions.set(key, { date: vacc.dateAdministered, vaccinations: [] });
      }
      sessions.get(key).vaccinations.push(vacc);
    });
    return Array.from(sessions.values());
  };

  const handleGenerate = () => {
    onGenerate(selectedIds);
    onClose();
  };

  const hasSelections = Object.values(selectedIds).some((ids) => ids.length > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Automated Discharge Summary</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : suggestions ? (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              The following records have not been used in a discharge summary. Select the ones you want to include.
            </Alert>

            {/* SOAP Notes */}
            {suggestions.soapNotes?.length > 0 && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom display="flex" alignItems="center" gap={1}>
                  <DescriptionIcon color="primary" />
                  SOAP Notes ({suggestions.soapNotes.length})
                </Typography>
                <List dense>
                  {suggestions.soapNotes.map((note: any) => (
                    <ListItem key={note.id} disablePadding>
                      <ListItemButton
                        onClick={() => toggleSelection('soapNoteIds', note.id)}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedIds.soapNoteIds.includes(note.id)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          // SoapNote.date is an INSTANT (DATE-KINDS §1.2: no picker,
                          // always `new Date().toISOString()` from SoapNoteDialog).
                          primary={formatInstant(note.date, INSTANT_FORMATS.dateTime)}
                          secondary={`Provider: ${note.provider?.name || 'Unknown'}`}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {/* Procedures */}
            {suggestions.procedures?.length > 0 && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom display="flex" alignItems="center" gap={1}>
                  <HealingIcon color="primary" />
                  Procedures ({suggestions.procedures.length})
                </Typography>
                <List dense>
                  {suggestions.procedures.map((procedure: any) => (
                    <ListItem key={procedure.id} disablePadding>
                      <ListItemButton
                        onClick={() => toggleSelection('procedureIds', procedure.id)}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedIds.procedureIds.includes(procedure.id)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={procedure.name}
                          // Procedure.date is MIXED at the column level (DATE-KINDS §1.2):
                          // the ProcedureDialog DateTimePicker writes an INSTANT, barn-ops
                          // bulk billing writes a UTC-midnight day. DATE-KINDS §2.3 files
                          // this site under INSTANT, and the label agrees — it has always
                          // printed a time of day, which only an instant has. The
                          // barn-ops arm stays imperfect either way (a UTC-midnight day
                          // reads as the previous evening in any negative-offset zone,
                          // browser or clinic); only the write side (§2.5) can settle it.
                          secondary={`${formatInstant(procedure.date, INSTANT_FORMATS.dateTime)} - ${procedure.category || 'N/A'}`}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {/* Medications */}
            {suggestions.medications?.length > 0 && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom display="flex" alignItems="center" gap={1}>
                  <MedicationIcon color="primary" />
                  Medications ({suggestions.medications.length})
                </Typography>
                <List dense>
                  {suggestions.medications.map((medication: any) => (
                    <ListItem key={medication.id} disablePadding>
                      <ListItemButton
                        onClick={() => toggleSelection('medicationIds', medication.id)}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedIds.medicationIds.includes(medication.id)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={medication.name}
                          // Medication.startDate is a CALENDAR DAY (DATE-KINDS §2.2 lists
                          // this exact site): the MedicationDialog DatePicker picks a day,
                          // so only the date PART may reach the screen — re-zoning it
                          // printed the previous day for every US clinic. ⚠ The column is
                          // MIXED (§1.2): SOAP auto-prescribe stamps the note's instant
                          // into it; that arm is a write-side fix, not a render one.
                          secondary={`${medication.dosage || ''} ${medication.frequency || ''} - Started: ${formatCalendarDate(medication.startDate, 'MMM d, yyyy') || 'Unknown'}`}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {/* Vaccinations - Grouped by Session */}
            {suggestions.vaccinations?.length > 0 && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom display="flex" alignItems="center" gap={1}>
                  <VaccineIcon color="primary" />
                  Vaccinations ({suggestions.vaccinations.length})
                </Typography>
                {groupVaccinationsBySession(suggestions.vaccinations).map((session, idx) => (
                  <Card key={idx} sx={{ mb: 2, border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
                    <CardContent>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {/* session.date is Vaccination.dateAdministered — MIXED at the
                            column level (DATE-KINDS §1.2): the web dialog never collects
                            it and both HorseProfile pages stamp `new Date().toISOString()`
                            (an INSTANT); only barn-ops bulk billing writes a UTC-midnight
                            day. Rendered with a time of day, so the instant arm is the one
                            this label describes; the clinic zone replaces the browser's.
                            The barn-ops arm needs a write-side fix (§2.5), not a helper. */}
                        Session: {formatInstant(session.date, INSTANT_FORMATS.dateTime)}
                      </Typography>
                      <List dense>
                        {session.vaccinations.map((vaccination: any) => (
                          <ListItem key={vaccination.id} disablePadding>
                            <ListItemButton
                              onClick={() => toggleSelection('vaccinationIds', vaccination.id)}
                            >
                              <ListItemIcon>
                                <Checkbox
                                  edge="start"
                                  checked={selectedIds.vaccinationIds.includes(vaccination.id)}
                                  tabIndex={-1}
                                  disableRipple
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={vaccination.vaccineName}
                                secondary={`${vaccination.manufacturer || 'N/A'} | ${vaccination.lotNumber || 'N/A'}`}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                ))}
              </Paper>
            )}

            {!suggestions.soapNotes?.length &&
              !suggestions.procedures?.length &&
              !suggestions.vaccinations?.length &&
              !suggestions.medications?.length && (
                <Alert severity="info">
                  No unlinked records found. All records have already been used in discharge summaries.
                </Alert>
              )}
          </Box>
        ) : (
          <Alert severity="error">Failed to load suggestions</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          disabled={!hasSelections}
          startIcon={<CheckIcon />}
        >
          Generate with AI
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutomationSuggestionsModal;

